import { Controller } from "@hotwired/stimulus"
import { loadPreferences } from "./user_preferences_controller"
import {
  WRITER_DOCUMENT_PARAM,
  forgetWriterConversation,
  rememberWriterConversation,
  rememberedWriterConversation,
  writerDocumentIdOf
} from "../utils/writer_conversations"

/**
 * Global Sidebar Controller
 *
 * Renders a fixed sidebar on the right edge of the viewport, independent of the page layout.
 * Two display modes:
 *   - "docked"   (default): pushes page content via body margin-right
 *   - "floating"          : floats over content with an inset, no body margin
 * Below MIN_VIEWPORT_WIDTH the sidebar is forced into floating mode regardless of preference,
 * so it never crowds the content on narrow viewports. The user's manual preference is preserved.
 * Supports drag-to-resize within configurable min/max bounds.
 * Preserved across navigations via data-turbo-permanent on the host element.
 */
const MIN_VIEWPORT_WIDTH = 1400
// Must match the --global-sidebar-floating-inset CSS variable in layout.css.
const FLOATING_INSET = 16
// Minimum number of pixels a manually-positioned panel must keep onscreen so
// it can never be dragged fully out of reach.
const MIN_VIEWPORT_MARGIN = 8
// Distance from a viewport edge at which the panel snaps back to anchored
// position on drag release.
const SNAP_THRESHOLD = 30
// Scroll backup persists outside the controller instance so it survives the
// brief Stimulus disconnect/reconnect that can happen while Turbo moves the
// permanent element between the old and new page bodies.
const SCROLL_BACKUP = new Map()

// Cross-file contract: server-side callers dispatch this event to ask the
// sidebar to reveal itself. Mirrored by AssistantHelper::ASSISTANT_SIDEBAR_OPEN_EVENT.
export const OPEN_REQUEST_EVENT = "global-sidebar:open-request"
export const PRELOAD_REQUEST_EVENT = "global-sidebar:preload-request"
export const TOGGLE_REQUEST_EVENT = "global-sidebar:toggle-request"
export const CLOSED_EVENT = "global-sidebar:closed"

export default class extends Controller {
  static targets = [
    "sidebar", "handle", "heightHandle", "topHandle", "rightHandle",
    "nwHandle", "neHandle", "swHandle", "seHandle"
  ]
  static values = {
    // Visibility on "general" pages — everywhere except the Writer doc edit page.
    visible: { type: Boolean, default: false },
    // Visibility on the Writer doc edit page. Persisted independently so the
    // forced-open behavior in Writer doesn't leak back into the general pages.
    writerVisible: { type: Boolean, default: true },
    width: { type: Number, default: 380 },
    minWidth: { type: Number, default: 300 },
    maxWidth: { type: Number, default: 600 },
    // Floating-mode panel height. 0 means "auto" (fill viewport minus insets);
    // any positive value applies an explicit height so the bottom edge can be dragged.
    height: { type: Number, default: 0 },
    minHeight: { type: Number, default: 240 },
    // Free positioning. When manuallyPositioned is true, the floating panel uses
    // explicit x/y coordinates (top-left in px from the viewport origin) instead
    // of the default top:inset/right:inset anchoring.
    manuallyPositioned: { type: Boolean, default: false },
    x: { type: Number, default: 0 },
    y: { type: Number, default: 0 },
    // Docked vs floating preference on general pages.
    mode: { type: String, default: "docked" },
    // Docked vs floating preference on Writer doc edit pages. Persisted
    // independently so toggling mode in one context doesn't bleed into the other.
    writerMode: { type: String, default: "docked" },
    conversationUrl: { type: String, default: "" },
    // Stable key of the resource the loaded conversation is anchored to
    // (e.g. "project:25"), stamped by conversations/show_tools and captured on
    // frame load. Persisted alongside conversationUrl so opening the sidebar on
    // a resource page can decide reuse-vs-new without a server round-trip.
    anchoredContextKey: { type: String, default: "" },
    // Paths under which the sidebar is hidden (it would be redundant with the
    // main assistant UI). visibleRoutes wins when both match — used to carve
    // out management subpages under /asistente where the sidebar is still
    // useful (e.g. /asistente/contextos, /asistente/especialistas).
    hiddenRoutes: { type: Array, default: ["/asistente"] },
    visibleRoutes: {
      type: Array,
      default: ["/asistente/contextos", "/asistente/especialistas", "/asistente/evaluaciones"]
    }
  }

  get viewportTooNarrow() {
    // Writer mode (the doc edit page at /redactor/:id/edit) auto-opens the
    // sidebar and provides its own layout, so the narrow-viewport guard does
    // not apply there. The /redactor index and other /redactor/* pages still
    // get the guard.
    if (this.isWriterEditRoute) return false
    return window.innerWidth < MIN_VIEWPORT_WIDTH
  }

  // True on the Writer doc edit page (/redactor/:id/edit). Used to read/write
  // a separate visibility slot so the "always-open in Writer" behavior doesn't
  // overwrite the user's preference on general pages.
  get isWriterEditRoute() {
    return /^\/redactor\/[^/]+\/edit(?:$|\/)/.test(window.location.pathname)
  }

  // Visibility slot for the current route. Writer doc edit pages persist their
  // own value via writerVisibleValue; everywhere else uses visibleValue.
  get effectiveVisible() {
    return this.isWriterEditRoute ? this.writerVisibleValue : this.visibleValue
  }

  setEffectiveVisible(value) {
    if (this.isWriterEditRoute) {
      this.writerVisibleValue = value
    } else {
      this.visibleValue = value
    }
  }

  // The user-set mode preference for the current route (docked vs floating).
  // Writer doc edit pages persist their own preference via writerModeValue.
  get currentModeValue() {
    return this.isWriterEditRoute ? this.writerModeValue : this.modeValue
  }

  setCurrentModeValue(value) {
    if (this.isWriterEditRoute) {
      this.writerModeValue = value
    } else {
      this.modeValue = value
    }
  }

  // The mode actually applied. Honors user preference, but forces "floating"
  // on narrow viewports so docked mode never crowds the content.
  get effectiveMode() {
    if (this.viewportTooNarrow) return "floating"
    return this.currentModeValue === "floating" ? "floating" : "docked"
  }

  // Width upper bound applied at resize time. In docked mode the configured
  // maxWidthValue keeps the panel from devouring the page; in floating mode
  // the panel can grow to fill the viewport minus the insets, since it doesn't
  // push content.
  get effectiveMaxWidth() {
    if (this.effectiveMode === "floating") {
      return window.innerWidth - 2 * FLOATING_INSET
    }
    return this.maxWidthValue
  }

  // The height actually applied to the floating panel. heightValue=0 means
  // "auto" (fill the viewport minus the two insets). Clamped to the viewport
  // so a stale persisted height never positions the bottom edge off-screen.
  get effectiveHeight() {
    const maxH = window.innerHeight - 2 * FLOATING_INSET
    if (this.heightValue > 0) return Math.max(this.minHeightValue, Math.min(maxH, this.heightValue))
    return maxH
  }

  // True only when the panel is in floating mode AND the user has explicitly
  // moved it. In docked or anchored-floating, geometry is dictated by CSS edge
  // anchoring; this flag opts in to absolute x/y/width/height positioning.
  get isManuallyPositioned() {
    return this.effectiveMode === "floating" && this.manuallyPositionedValue
  }

  // Computes the viewport-coordinate rectangle the panel actually occupies,
  // *without* reading the DOM. Used to position the resize handles consistently
  // across docked / anchored-floating / manually-positioned states.
  get panelGeometry() {
    const width = this.widthValue
    const mode = this.effectiveMode
    if (mode === "docked") {
      return { left: window.innerWidth - width, top: 0, width, height: window.innerHeight }
    }
    const height = this.effectiveHeight
    if (this.isManuallyPositioned) {
      return { left: this.xValue, top: this.yValue, width, height }
    }
    return {
      left: window.innerWidth - FLOATING_INSET - width,
      top: FLOATING_INSET,
      width,
      height
    }
  }

  initialize() {
    loadPreferences(this)
  }

  connect() {
    this.resizing = false
    this.animationFrameId = null
    this.hiddenByRoute = false
    this.lastWasWriterRoute = this.isWriterEditRoute

    this.boundHandlers = {
      keydown: this.handleKeydown.bind(this),
      toggleRequest: this.handleToggleRequest.bind(this),
      mouseMove: this.handleMouseMove.bind(this),
      mouseUp: this.handleMouseUp.bind(this),
      verticalMouseMove: this.handleVerticalMouseMove.bind(this),
      verticalMouseUp: this.handleVerticalMouseUp.bind(this),
      topMouseMove: this.handleTopMouseMove.bind(this),
      topMouseUp: this.handleTopMouseUp.bind(this),
      rightMouseMove: this.handleRightMouseMove.bind(this),
      rightMouseUp: this.handleRightMouseUp.bind(this),
      cornerMouseMove: this.handleCornerMouseMove.bind(this),
      cornerMouseUp: this.handleCornerMouseUp.bind(this),
      dragMouseMove: this.handleDragMouseMove.bind(this),
      dragMouseUp: this.handleDragMouseUp.bind(this),
      dragStartRequest: this.handleDragStartRequest.bind(this),
      turboLoad: this.handleTurboLoad.bind(this),
      beforeRender: this.saveScrollPositions.bind(this),
      afterRender: this.restoreScrollPositions.bind(this),
      resize: this.handleResize.bind(this)
    }

    document.addEventListener("keydown", this.boundHandlers.keydown)
    document.addEventListener(TOGGLE_REQUEST_EVENT, this.boundHandlers.toggleRequest)
    document.addEventListener(OPEN_REQUEST_EVENT, this.boundHandlers.openRequest = this.handleOpenRequest.bind(this))
    document.addEventListener(PRELOAD_REQUEST_EVENT, this.boundHandlers.preloadRequest = this.handlePreloadRequest.bind(this))
    document.addEventListener("global-sidebar:toggle-mode-request", this.boundHandlers.toggleModeRequest = this.handleToggleModeRequest.bind(this))
    document.addEventListener("global-sidebar:drag-start-request", this.boundHandlers.dragStartRequest)
    document.addEventListener("global-sidebar:reset-position-request", this.boundHandlers.resetPositionRequest = this.handleResetPositionRequest.bind(this))
    document.addEventListener("turbo:load", this.boundHandlers.turboLoad)
    document.addEventListener("turbo:before-render", this.boundHandlers.beforeRender)
    document.addEventListener("turbo:render", this.boundHandlers.afterRender)
    window.addEventListener("resize", this.boundHandlers.resize, { passive: true })

    // Restore saved conversation URL on hard reload
    this.restoreConversationUrl()

    // Strip transitions before applying state to prevent flash on reconnect
    this.sidebarTarget.classList.remove("animated")
    this.handleTarget.classList.remove("animated")
    document.body.classList.remove("global-sidebar-animated")

    // Apply initial state synchronously — must happen before first paint
    this.checkRouteVisibility()
    this.applyState()
    document.body.classList.remove("global-sidebar-prepaint")

    // Track conversation frame navigations to persist the URL
    this.boundHandlers.frameLoad = this.handleFrameLoad.bind(this)
    this.boundHandlers.frameMissing = this.handleFrameMissing.bind(this)
    this.element.addEventListener("turbo:frame-load", this.boundHandlers.frameLoad)
    this.element.addEventListener("turbo:frame-missing", this.boundHandlers.frameMissing)

    // Prefetch the conversation panel on the first sign the user is heading for
    // it, so opening reveals a ready panel instead of a spinner. Delegated on
    // document (pointerover bubbles, pointerenter does not) so it keeps working
    // after Turbo swaps the trigger and rail on navigation.
    this.boundHandlers.prefetch = this.handlePrefetchIntent.bind(this)
    document.addEventListener("pointerover", this.boundHandlers.prefetch, { passive: true })
    document.addEventListener("focusin", this.boundHandlers.prefetch)

    // Enable transitions for subsequent toggles (after initial state is painted)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.sidebarTarget.classList.add("animated")
        this.handleTarget.classList.add("animated")
        document.body.classList.add("global-sidebar-animated")
      })
    })
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundHandlers.keydown)
    document.removeEventListener(TOGGLE_REQUEST_EVENT, this.boundHandlers.toggleRequest)
    document.removeEventListener(OPEN_REQUEST_EVENT, this.boundHandlers.openRequest)
    document.removeEventListener(PRELOAD_REQUEST_EVENT, this.boundHandlers.preloadRequest)
    document.removeEventListener("global-sidebar:toggle-mode-request", this.boundHandlers.toggleModeRequest)
    document.removeEventListener("global-sidebar:drag-start-request", this.boundHandlers.dragStartRequest)
    document.removeEventListener("global-sidebar:reset-position-request", this.boundHandlers.resetPositionRequest)
    document.removeEventListener("turbo:load", this.boundHandlers.turboLoad)
    document.removeEventListener("turbo:before-render", this.boundHandlers.beforeRender)
    document.removeEventListener("turbo:render", this.boundHandlers.afterRender)
    this.element.removeEventListener("turbo:frame-load", this.boundHandlers.frameLoad)
    this.element.removeEventListener("turbo:frame-missing", this.boundHandlers.frameMissing)
    document.removeEventListener("pointerover", this.boundHandlers.prefetch)
    document.removeEventListener("focusin", this.boundHandlers.prefetch)
    window.removeEventListener("resize", this.boundHandlers.resize)
    window.removeEventListener("mousemove", this.boundHandlers.mouseMove)
    window.removeEventListener("mouseup", this.boundHandlers.mouseUp)
    window.removeEventListener("touchmove", this.boundHandlers.mouseMove)
    window.removeEventListener("touchend", this.boundHandlers.mouseUp)
    window.removeEventListener("mousemove", this.boundHandlers.verticalMouseMove)
    window.removeEventListener("mouseup", this.boundHandlers.verticalMouseUp)
    window.removeEventListener("touchmove", this.boundHandlers.verticalMouseMove)
    window.removeEventListener("touchend", this.boundHandlers.verticalMouseUp)
    window.removeEventListener("mousemove", this.boundHandlers.dragMouseMove)
    window.removeEventListener("mouseup", this.boundHandlers.dragMouseUp)
    window.removeEventListener("touchmove", this.boundHandlers.dragMouseMove)
    window.removeEventListener("touchend", this.boundHandlers.dragMouseUp)
    window.removeEventListener("mousemove", this.boundHandlers.topMouseMove)
    window.removeEventListener("mouseup", this.boundHandlers.topMouseUp)
    window.removeEventListener("touchmove", this.boundHandlers.topMouseMove)
    window.removeEventListener("touchend", this.boundHandlers.topMouseUp)
    window.removeEventListener("mousemove", this.boundHandlers.rightMouseMove)
    window.removeEventListener("mouseup", this.boundHandlers.rightMouseUp)
    window.removeEventListener("touchmove", this.boundHandlers.rightMouseMove)
    window.removeEventListener("touchend", this.boundHandlers.rightMouseUp)
    window.removeEventListener("mousemove", this.boundHandlers.cornerMouseMove)
    window.removeEventListener("mouseup", this.boundHandlers.cornerMouseUp)
    window.removeEventListener("touchmove", this.boundHandlers.cornerMouseMove)
    window.removeEventListener("touchend", this.boundHandlers.cornerMouseUp)

    if (this.animationFrameId) cancelAnimationFrame(this.animationFrameId)
    if (this.verticalAnimationFrameId) cancelAnimationFrame(this.verticalAnimationFrameId)
    if (this.dragAnimationFrameId) cancelAnimationFrame(this.dragAnimationFrameId)
    if (this.topAnimationFrameId) cancelAnimationFrame(this.topAnimationFrameId)
    if (this.rightAnimationFrameId) cancelAnimationFrame(this.rightAnimationFrameId)
    if (this.cornerAnimationFrameId) cancelAnimationFrame(this.cornerAnimationFrameId)

    document.body.classList.remove("global-sidebar-animated")
    document.body.classList.remove("global-sidebar-trigger-hidden")
    document.body.classList.remove("global-sidebar-mode-docked")
    document.body.classList.remove("global-sidebar-mode-floating")
    document.body.classList.remove("global-sidebar-mode-forced")
    document.body.classList.remove("global-sidebar-positioned-manually")
  }

  toggle() {
    if (this.hiddenByRoute) return
    const willOpen = !this.effectiveVisible
    this.setEffectiveVisible(willOpen)
    this.applyState()
    if (willOpen) this.applyPageContextOnOpen()
    else document.dispatchEvent(new CustomEvent(CLOSED_EVENT))
  }

  toggleMode() {
    this.setCurrentModeValue(this.currentModeValue === "floating" ? "docked" : "floating")
    this.applyState()
  }

  applyState() {
    this.sidebarTarget.style.width = `${this.widthValue}px`

    const mode = this.effectiveMode
    this.sidebarTarget.dataset.mode = mode
    document.body.classList.toggle("global-sidebar-mode-floating", mode === "floating")
    document.body.classList.toggle("global-sidebar-mode-docked", mode === "docked")
    // Mode is "forced" when the viewport, not the user, dictates floating —
    // we use this to hide the dock/float toggle since docked is unavailable.
    document.body.classList.toggle("global-sidebar-mode-forced", this.viewportTooNarrow)
    document.body.classList.toggle("global-sidebar-positioned-manually", this.isManuallyPositioned)

    this.applyPanelPosition(mode)
    this.applyPanelHeight(mode)

    const effectivelyVisible = this.effectiveVisible && !this.hiddenByRoute

    this.sidebarTarget.inert = !effectivelyVisible

    if (effectivelyVisible) {
      this.sidebarTarget.classList.add("visible")
      this.loadConversationFrameIfNeeded()
      this.focusPreloadedComposer()
      this.pushContent(mode === "docked" ? this.widthValue : 0)
      this.applyHandlePositions()
      this.updateCssVariable()
    } else {
      this.sidebarTarget.classList.remove("visible")
      this.handleTarget.classList.remove("visible")
      this.heightHandleTarget.classList.remove("visible")
      this.topHandleTarget.classList.remove("visible")
      this.rightHandleTarget.classList.remove("visible")
      this._cornerTargets.forEach(t => t.classList.remove("visible"))
      this.pushContent(0)
      document.documentElement.style.setProperty("--global-sidebar-width", "0px")
    }

    document.body.classList.toggle("global-sidebar-trigger-hidden", effectivelyVisible || this.hiddenByRoute)
  }

  // Applies explicit top/left when manually positioned; otherwise clears the
  // inline styles so the CSS edge anchoring (docked / floating-default) takes over.
  applyPanelPosition(mode) {
    if (mode === "floating" && this.manuallyPositionedValue) {
      this.sidebarTarget.dataset.position = "manual"
      this.sidebarTarget.style.top = `${this.yValue}px`
      this.sidebarTarget.style.left = `${this.xValue}px`
      this.sidebarTarget.style.right = "auto"
      this.sidebarTarget.style.bottom = "auto"
    } else {
      this.sidebarTarget.dataset.position = "anchored"
      this.sidebarTarget.style.top = ""
      this.sidebarTarget.style.left = ""
      this.sidebarTarget.style.right = ""
      this.sidebarTarget.style.bottom = ""
    }
  }

  // Applies an explicit height in floating mode when the user has dragged the
  // bottom edge, or always when manually positioned (since absolute position
  // detaches the panel from the bottom anchor).
  applyPanelHeight(mode) {
    const needsExplicitHeight = mode === "floating" && (this.heightValue > 0 || this.manuallyPositionedValue)
    if (needsExplicitHeight) {
      this.sidebarTarget.style.height = `${this.effectiveHeight}px`
      if (!this.manuallyPositionedValue) {
        this.sidebarTarget.style.bottom = "auto"
      }
    } else {
      this.sidebarTarget.style.height = ""
      if (!this.manuallyPositionedValue) {
        this.sidebarTarget.style.bottom = ""
      }
    }
  }

  pushContent(width) {
    document.body.style.marginRight = width ? `${width}px` : ""
  }

  // Positions all resize handles relative to the panel's current geometry.
  // Works uniformly for docked, anchored-floating, and manually-positioned modes.
  applyHandlePositions() {
    const g = this.panelGeometry
    const mode = this.effectiveMode
    const isManual = this.isManuallyPositioned

    // Left edge — always available (width drag in all modes).
    this.handleTarget.classList.add("visible")
    this.handleTarget.style.left = `${g.left}px`
    this.handleTarget.style.top = `${g.top}px`
    this.handleTarget.style.height = `${g.height}px`
    this.handleTarget.style.right = "auto"
    this.handleTarget.style.bottom = "auto"

    // Bottom edge — only meaningful in floating.
    const show = mode === "floating"
    this.heightHandleTarget.classList.toggle("visible", show)
    if (show) {
      this.heightHandleTarget.style.top = `${g.top + g.height}px`
      this.heightHandleTarget.style.left = `${g.left}px`
      this.heightHandleTarget.style.width = `${g.width}px`
      this.heightHandleTarget.style.right = "auto"
    }

    // Top + right edges and corner handles are visible in any floating mode,
    // not just manual. Clicking them in anchored mode flips into manual using
    // the current geometry — corners are then discoverable from the default
    // floating state without needing to drag the header first.
    const showExtraHandles = mode === "floating"
    this.topHandleTarget.classList.toggle("visible", showExtraHandles)
    if (showExtraHandles) {
      this.topHandleTarget.style.top = `${g.top}px`
      this.topHandleTarget.style.left = `${g.left}px`
      this.topHandleTarget.style.width = `${g.width}px`
    }
    this.rightHandleTarget.classList.toggle("visible", showExtraHandles)
    if (showExtraHandles) {
      this.rightHandleTarget.style.left = `${g.left + g.width}px`
      this.rightHandleTarget.style.top = `${g.top}px`
      this.rightHandleTarget.style.height = `${g.height}px`
    }

    const cornerSpecs = [
      ["nw", g.left,           g.top],
      ["ne", g.left + g.width, g.top],
      ["sw", g.left,           g.top + g.height],
      ["se", g.left + g.width, g.top + g.height]
    ]
    cornerSpecs.forEach(([corner, x, y]) => {
      const target = this[`${corner}HandleTarget`]
      target.classList.toggle("visible", showExtraHandles)
      if (showExtraHandles) {
        target.style.left = `${x}px`
        target.style.top = `${y}px`
      }
    })
  }

  // Promotes the panel from anchored-floating to manually-positioned using its
  // current geometry, so handles that conceptually need free positioning
  // (top edge, right edge, corners) work straight from the anchored default.
  _ensureManualPositioning() {
    if (this.manuallyPositionedValue) return
    const g = this.panelGeometry
    this.manuallyPositionedValue = true
    this.xValue = g.left
    this.yValue = g.top
  }

  get _cornerTargets() {
    return [this.nwHandleTarget, this.neHandleTarget, this.swHandleTarget, this.seHandleTarget]
  }

  updateCssVariable() {
    document.documentElement.style.setProperty("--global-sidebar-width", `${this.widthValue}px`)
  }

  // --- Resize ---

  startResize(event) {
    event.preventDefault()
    this.resizing = true
    this.startX = event.clientX || event.touches[0].clientX
    this.startWidth = this.widthValue
    // Needed in manual mode so the right edge stays anchored while the left
    // edge follows the cursor.
    this.startLeft = this.xValue

    this.handleTarget.classList.add("active")

    document.body.classList.remove("global-sidebar-animated")

    window.addEventListener("mousemove", this.boundHandlers.mouseMove, { passive: true })
    window.addEventListener("mouseup", this.boundHandlers.mouseUp)
    window.addEventListener("touchmove", this.boundHandlers.mouseMove, { passive: true })
    window.addEventListener("touchend", this.boundHandlers.mouseUp)

    document.body.style.userSelect = "none"
    document.body.style.cursor = "col-resize"

    this.animationFrameId = requestAnimationFrame(this.updateLayout.bind(this))
  }

  handleMouseMove(event) {
    if (!this.resizing) return
    const clientX = event.clientX || (event.touches && event.touches[0].clientX)
    if (!clientX) return

    const deltaX = this.startX - clientX
    this.currentWidth = Math.max(
      this.minWidthValue,
      Math.min(this.effectiveMaxWidth, this.startWidth + deltaX)
    )
    // When manually positioned, the left edge moves with the cursor and the
    // right edge stays anchored. Recompute x so the panel's right edge stays
    // put (anchored modes keep right anchored via CSS, so x is not needed).
    if (this.isManuallyPositioned) {
      const rightEdge = this.startLeft + this.startWidth
      this.currentLeft = rightEdge - this.currentWidth
    }
  }

  updateLayout() {
    if (!this.resizing) return

    if (this.currentWidth) {
      const mode = this.effectiveMode
      this.sidebarTarget.style.width = `${this.currentWidth}px`
      this.pushContent(mode === "docked" ? this.currentWidth : 0)
      // Recompute panel.left from the in-flight width so handles track the live edge.
      const liveLeft = this.isManuallyPositioned
        ? this.currentLeft
        : (mode === "docked" ? window.innerWidth - this.currentWidth
                             : window.innerWidth - FLOATING_INSET - this.currentWidth)
      if (this.isManuallyPositioned) {
        this.sidebarTarget.style.left = `${liveLeft}px`
      }
      this.handleTarget.style.left = `${liveLeft}px`
      if (mode === "floating") {
        this.heightHandleTarget.style.left = `${liveLeft}px`
        this.heightHandleTarget.style.width = `${this.currentWidth}px`
      }
      if (this.isManuallyPositioned) {
        this.topHandleTarget.style.left = `${liveLeft}px`
        this.topHandleTarget.style.width = `${this.currentWidth}px`
        this.rightHandleTarget.style.left = `${liveLeft + this.currentWidth}px`
      }
      document.documentElement.style.setProperty("--global-sidebar-width", `${this.currentWidth}px`)
    }

    this.animationFrameId = requestAnimationFrame(this.updateLayout.bind(this))
  }

  // --- Vertical resize (floating mode only) ---

  startVerticalResize(event) {
    if (this.effectiveMode !== "floating") return
    event.preventDefault()
    this.verticalResizing = true
    this.startY = event.clientY || event.touches[0].clientY
    this.startHeight = this.effectiveHeight

    this.heightHandleTarget.classList.add("active")

    this.sidebarTarget.classList.remove("animated")

    window.addEventListener("mousemove", this.boundHandlers.verticalMouseMove, { passive: true })
    window.addEventListener("mouseup", this.boundHandlers.verticalMouseUp)
    window.addEventListener("touchmove", this.boundHandlers.verticalMouseMove, { passive: true })
    window.addEventListener("touchend", this.boundHandlers.verticalMouseUp)

    document.body.style.userSelect = "none"
    document.body.style.cursor = "row-resize"

    this.verticalAnimationFrameId = requestAnimationFrame(this.updateVerticalLayout.bind(this))
  }

  handleVerticalMouseMove(event) {
    if (!this.verticalResizing) return
    const clientY = event.clientY || (event.touches && event.touches[0].clientY)
    if (!clientY) return

    const deltaY = clientY - this.startY  // drag down → positive → grow
    const maxH = window.innerHeight - 2 * FLOATING_INSET
    this.currentHeight = Math.max(
      this.minHeightValue,
      Math.min(maxH, this.startHeight + deltaY)
    )
  }

  updateVerticalLayout() {
    if (!this.verticalResizing) return

    if (this.currentHeight) {
      this.sidebarTarget.style.height = `${this.currentHeight}px`
      if (!this.manuallyPositionedValue) {
        this.sidebarTarget.style.bottom = "auto"
      }
      const liveTop = this.isManuallyPositioned ? this.yValue : FLOATING_INSET
      this.heightHandleTarget.style.top = `${liveTop + this.currentHeight}px`
    }

    this.verticalAnimationFrameId = requestAnimationFrame(this.updateVerticalLayout.bind(this))
  }

  handleVerticalMouseUp() {
    if (!this.verticalResizing) return
    this.verticalResizing = false

    if (this.currentHeight) {
      this.heightValue = this.currentHeight
    }

    this.heightHandleTarget.classList.remove("active")

    cancelAnimationFrame(this.verticalAnimationFrameId)
    this.verticalAnimationFrameId = null

    window.removeEventListener("mousemove", this.boundHandlers.verticalMouseMove)
    window.removeEventListener("mouseup", this.boundHandlers.verticalMouseUp)
    window.removeEventListener("touchmove", this.boundHandlers.verticalMouseMove)
    window.removeEventListener("touchend", this.boundHandlers.verticalMouseUp)

    document.body.style.userSelect = ""
    document.body.style.cursor = ""

    this.sidebarTarget.classList.add("animated")
  }

  handleMouseUp() {
    if (!this.resizing) return
    this.resizing = false

    if (this.currentWidth) {
      this.widthValue = this.currentWidth
      if (this.isManuallyPositioned && this.currentLeft != null) {
        this.xValue = this.currentLeft
      }
    }
    this.currentLeft = null

    this.handleTarget.classList.remove("active")

    cancelAnimationFrame(this.animationFrameId)
    this.animationFrameId = null

    window.removeEventListener("mousemove", this.boundHandlers.mouseMove)
    window.removeEventListener("mouseup", this.boundHandlers.mouseUp)
    window.removeEventListener("touchmove", this.boundHandlers.mouseMove)
    window.removeEventListener("touchend", this.boundHandlers.mouseUp)

    document.body.style.userSelect = ""
    document.body.style.cursor = ""

    document.body.classList.add("global-sidebar-animated")
  }

  // --- Reposition (drag from header, floating mode only) ---

  handleDragStartRequest(event) {
    if (this.effectiveMode !== "floating") return
    if (!this.effectiveVisible) return
    const { clientX, clientY } = event.detail || {}
    if (clientX == null || clientY == null) return
    this.startDrag(clientX, clientY)
  }

  startDrag(clientX, clientY) {
    const g = this.panelGeometry
    this.dragging = true
    // Switch into manual positioning so we can use absolute top/left from now on.
    this.manuallyPositionedValue = true
    this.xValue = g.left
    this.yValue = g.top
    // Cursor offset relative to the panel's top-left corner so the panel keeps
    // its grip point under the pointer throughout the drag.
    this.dragOffsetX = clientX - g.left
    this.dragOffsetY = clientY - g.top

    this.sidebarTarget.classList.remove("animated")
    document.body.classList.remove("global-sidebar-animated")

    window.addEventListener("mousemove", this.boundHandlers.dragMouseMove, { passive: true })
    window.addEventListener("mouseup", this.boundHandlers.dragMouseUp)
    window.addEventListener("touchmove", this.boundHandlers.dragMouseMove, { passive: true })
    window.addEventListener("touchend", this.boundHandlers.dragMouseUp)

    document.body.style.userSelect = "none"
    document.body.style.cursor = "grabbing"

    // First applyState writes the manual position into the panel so subsequent
    // mousemove updates can lerp from there.
    this.applyState()
    this.dragAnimationFrameId = requestAnimationFrame(this.updateDragLayout.bind(this))
  }

  handleDragMouseMove(event) {
    if (!this.dragging) return
    const point = event.touches ? event.touches[0] : event
    if (!point) return

    const width = this.widthValue
    const height = this.effectiveHeight
    const maxX = window.innerWidth - width - MIN_VIEWPORT_MARGIN
    const maxY = window.innerHeight - height - MIN_VIEWPORT_MARGIN

    this.currentX = Math.max(MIN_VIEWPORT_MARGIN, Math.min(maxX, point.clientX - this.dragOffsetX))
    this.currentY = Math.max(MIN_VIEWPORT_MARGIN, Math.min(maxY, point.clientY - this.dragOffsetY))
  }

  updateDragLayout() {
    if (!this.dragging) return

    if (this.currentX != null && this.currentY != null) {
      this.sidebarTarget.style.left = `${this.currentX}px`
      this.sidebarTarget.style.top = `${this.currentY}px`
      this.handleTarget.style.left = `${this.currentX}px`
      this.handleTarget.style.top = `${this.currentY}px`
      this.handleTarget.style.height = `${this.effectiveHeight}px`
      this.heightHandleTarget.style.left = `${this.currentX}px`
      this.heightHandleTarget.style.top = `${this.currentY + this.effectiveHeight}px`
      this.heightHandleTarget.style.width = `${this.widthValue}px`
    }

    this.dragAnimationFrameId = requestAnimationFrame(this.updateDragLayout.bind(this))
  }

  handleDragMouseUp() {
    if (!this.dragging) return
    this.dragging = false

    if (this.currentX != null && this.currentY != null) {
      this.xValue = this.currentX
      this.yValue = this.currentY
    }
    this.currentX = null
    this.currentY = null

    cancelAnimationFrame(this.dragAnimationFrameId)
    this.dragAnimationFrameId = null

    window.removeEventListener("mousemove", this.boundHandlers.dragMouseMove)
    window.removeEventListener("mouseup", this.boundHandlers.dragMouseUp)
    window.removeEventListener("touchmove", this.boundHandlers.dragMouseMove)
    window.removeEventListener("touchend", this.boundHandlers.dragMouseUp)

    document.body.style.userSelect = ""
    document.body.style.cursor = ""

    this.maybeSnapToEdges()

    this.sidebarTarget.classList.add("animated")
    document.body.classList.add("global-sidebar-animated")
    this.applyState()
  }

  // If the panel was released within SNAP_THRESHOLD of any viewport edge, treat
  // it as a "throw back" gesture: revert to anchored positioning (top-right by
  // default). Snapping to other edges would require asymmetric anchoring; we
  // keep the simple "right edge" behaviour because it's the docked baseline.
  maybeSnapToEdges() {
    if (!this.manuallyPositionedValue) return
    const width = this.widthValue
    const distanceToRight = window.innerWidth - (this.xValue + width)
    const distanceToTop = this.yValue

    if (distanceToRight <= SNAP_THRESHOLD && distanceToTop <= SNAP_THRESHOLD * 2) {
      this.manuallyPositionedValue = false
    }
  }

  // --- Top edge resize (manual positioning only). Drag up → y decreases, height grows. ---

  startTopResize(event) {
    if (this.effectiveMode !== "floating") return
    event.preventDefault()
    this._ensureManualPositioning()
    this.topResizing = true
    this.startY = event.clientY || event.touches[0].clientY
    this.startHeight = this.effectiveHeight
    this.startTop = this.yValue
    this.topHandleTarget.classList.add("active")
    this.sidebarTarget.classList.remove("animated")
    window.addEventListener("mousemove", this.boundHandlers.topMouseMove, { passive: true })
    window.addEventListener("mouseup", this.boundHandlers.topMouseUp)
    window.addEventListener("touchmove", this.boundHandlers.topMouseMove, { passive: true })
    window.addEventListener("touchend", this.boundHandlers.topMouseUp)
    document.body.style.userSelect = "none"
    document.body.style.cursor = "row-resize"
    this.topAnimationFrameId = requestAnimationFrame(this.updateTopLayout.bind(this))
  }

  handleTopMouseMove(event) {
    if (!this.topResizing) return
    const clientY = event.clientY || (event.touches && event.touches[0].clientY)
    if (clientY == null) return
    const deltaY = clientY - this.startY  // drag down → positive → top moves down, height shrinks
    // New top is clamped so the bottom edge stays put and minHeight is respected.
    const bottomEdge = this.startTop + this.startHeight
    const newTop = Math.max(
      MIN_VIEWPORT_MARGIN,
      Math.min(bottomEdge - this.minHeightValue, this.startTop + deltaY)
    )
    this.currentTop = newTop
    this.currentHeight = bottomEdge - newTop
  }

  updateTopLayout() {
    if (!this.topResizing) return
    if (this.currentTop != null) {
      this.sidebarTarget.style.top = `${this.currentTop}px`
      this.sidebarTarget.style.height = `${this.currentHeight}px`
      this._repositionHandlesDuringDrag({ top: this.currentTop, height: this.currentHeight })
    }
    this.topAnimationFrameId = requestAnimationFrame(this.updateTopLayout.bind(this))
  }

  handleTopMouseUp() {
    if (!this.topResizing) return
    this.topResizing = false
    if (this.currentTop != null) {
      this.yValue = this.currentTop
      this.heightValue = this.currentHeight
    }
    this.currentTop = null
    this.topHandleTarget.classList.remove("active")
    cancelAnimationFrame(this.topAnimationFrameId)
    this.topAnimationFrameId = null
    window.removeEventListener("mousemove", this.boundHandlers.topMouseMove)
    window.removeEventListener("mouseup", this.boundHandlers.topMouseUp)
    window.removeEventListener("touchmove", this.boundHandlers.topMouseMove)
    window.removeEventListener("touchend", this.boundHandlers.topMouseUp)
    document.body.style.userSelect = ""
    document.body.style.cursor = ""
    this.sidebarTarget.classList.add("animated")
  }

  // --- Right edge resize (manual positioning only). Drag right → x stays, width grows. ---

  startRightResize(event) {
    if (this.effectiveMode !== "floating") return
    event.preventDefault()
    this._ensureManualPositioning()
    this.rightResizing = true
    this.startX = event.clientX || event.touches[0].clientX
    this.startWidth = this.widthValue
    this.rightHandleTarget.classList.add("active")
    this.sidebarTarget.classList.remove("animated")
    window.addEventListener("mousemove", this.boundHandlers.rightMouseMove, { passive: true })
    window.addEventListener("mouseup", this.boundHandlers.rightMouseUp)
    window.addEventListener("touchmove", this.boundHandlers.rightMouseMove, { passive: true })
    window.addEventListener("touchend", this.boundHandlers.rightMouseUp)
    document.body.style.userSelect = "none"
    document.body.style.cursor = "col-resize"
    this.rightAnimationFrameId = requestAnimationFrame(this.updateRightLayout.bind(this))
  }

  handleRightMouseMove(event) {
    if (!this.rightResizing) return
    const clientX = event.clientX || (event.touches && event.touches[0].clientX)
    if (clientX == null) return
    const deltaX = clientX - this.startX  // drag right → positive → width grows
    const maxW = Math.min(this.effectiveMaxWidth, window.innerWidth - this.xValue - MIN_VIEWPORT_MARGIN)
    this.currentWidth = Math.max(this.minWidthValue, Math.min(maxW, this.startWidth + deltaX))
  }

  updateRightLayout() {
    if (!this.rightResizing) return
    if (this.currentWidth) {
      this.sidebarTarget.style.width = `${this.currentWidth}px`
      this._repositionHandlesDuringDrag({ width: this.currentWidth })
    }
    this.rightAnimationFrameId = requestAnimationFrame(this.updateRightLayout.bind(this))
  }

  handleRightMouseUp() {
    if (!this.rightResizing) return
    this.rightResizing = false
    if (this.currentWidth) this.widthValue = this.currentWidth
    this.rightHandleTarget.classList.remove("active")
    cancelAnimationFrame(this.rightAnimationFrameId)
    this.rightAnimationFrameId = null
    window.removeEventListener("mousemove", this.boundHandlers.rightMouseMove)
    window.removeEventListener("mouseup", this.boundHandlers.rightMouseUp)
    window.removeEventListener("touchmove", this.boundHandlers.rightMouseMove)
    window.removeEventListener("touchend", this.boundHandlers.rightMouseUp)
    document.body.style.userSelect = ""
    document.body.style.cursor = ""
    this.sidebarTarget.classList.add("animated")
  }

  // Shared helper: during a top/right resize drag, mirror the in-flight geometry
  // onto the other handles so they don't lag behind the panel. Pass in only the
  // axes that changed; the rest fall back to current values.
  _repositionHandlesDuringDrag({ top = this.yValue, left = this.xValue, width = this.widthValue, height = this.effectiveHeight } = {}) {
    this.handleTarget.style.left = `${left}px`
    this.handleTarget.style.top = `${top}px`
    this.handleTarget.style.height = `${height}px`
    this.heightHandleTarget.style.left = `${left}px`
    this.heightHandleTarget.style.top = `${top + height}px`
    this.heightHandleTarget.style.width = `${width}px`
    this.topHandleTarget.style.left = `${left}px`
    this.topHandleTarget.style.top = `${top}px`
    this.topHandleTarget.style.width = `${width}px`
    this.rightHandleTarget.style.left = `${left + width}px`
    this.rightHandleTarget.style.top = `${top}px`
    this.rightHandleTarget.style.height = `${height}px`
    const cornerSpecs = [
      ["nw", left,          top],
      ["ne", left + width,  top],
      ["sw", left,          top + height],
      ["se", left + width,  top + height]
    ]
    cornerSpecs.forEach(([corner, x, y]) => {
      const target = this[`${corner}HandleTarget`]
      target.style.left = `${x}px`
      target.style.top = `${y}px`
    })
  }

  // --- Corner resize (manual positioning only). Drag updates two axes at once. ---

  startCornerResize(event) {
    if (this.effectiveMode !== "floating") return
    const corner = event.params.corner
    event.preventDefault()
    this._ensureManualPositioning()

    this.cornerResizing = true
    this.activeCorner = corner
    const point = event.touches ? event.touches[0] : event
    this.startX = point.clientX
    this.startY = point.clientY
    this.startLeft = this.xValue
    this.startTop = this.yValue
    this.startWidth = this.widthValue
    this.startHeight = this.effectiveHeight

    this[`${corner}HandleTarget`].classList.add("active")
    this.sidebarTarget.classList.remove("animated")

    window.addEventListener("mousemove", this.boundHandlers.cornerMouseMove, { passive: true })
    window.addEventListener("mouseup", this.boundHandlers.cornerMouseUp)
    window.addEventListener("touchmove", this.boundHandlers.cornerMouseMove, { passive: true })
    window.addEventListener("touchend", this.boundHandlers.cornerMouseUp)

    document.body.style.userSelect = "none"
    document.body.style.cursor = (corner === "nw" || corner === "se") ? "nwse-resize" : "nesw-resize"

    this.cornerAnimationFrameId = requestAnimationFrame(this.updateCornerLayout.bind(this))
  }

  handleCornerMouseMove(event) {
    if (!this.cornerResizing) return
    const point = event.touches ? event.touches[0] : event
    if (!point) return

    const deltaX = point.clientX - this.startX
    const deltaY = point.clientY - this.startY
    const corner = this.activeCorner

    let newLeft = this.startLeft
    let newTop = this.startTop
    let newWidth = this.startWidth
    let newHeight = this.startHeight

    // Horizontal axis: NW/SW move the left edge (anchor right), NE/SE move width (anchor left).
    if (corner === "nw" || corner === "sw") {
      const rightEdge = this.startLeft + this.startWidth
      const maxLeft = rightEdge - this.minWidthValue
      const minLeft = Math.max(MIN_VIEWPORT_MARGIN, rightEdge - this.effectiveMaxWidth)
      newLeft = Math.max(minLeft, Math.min(maxLeft, this.startLeft + deltaX))
      newWidth = rightEdge - newLeft
    } else {
      const maxW = Math.min(this.effectiveMaxWidth, window.innerWidth - this.startLeft - MIN_VIEWPORT_MARGIN)
      newWidth = Math.max(this.minWidthValue, Math.min(maxW, this.startWidth + deltaX))
    }

    // Vertical axis: NW/NE move the top edge (anchor bottom), SW/SE move height (anchor top).
    if (corner === "nw" || corner === "ne") {
      const bottomEdge = this.startTop + this.startHeight
      const maxTop = bottomEdge - this.minHeightValue
      newTop = Math.max(MIN_VIEWPORT_MARGIN, Math.min(maxTop, this.startTop + deltaY))
      newHeight = bottomEdge - newTop
    } else {
      const maxH = window.innerHeight - this.startTop - MIN_VIEWPORT_MARGIN
      newHeight = Math.max(this.minHeightValue, Math.min(maxH, this.startHeight + deltaY))
    }

    this.currentLeft = newLeft
    this.currentTop = newTop
    this.currentWidth = newWidth
    this.currentHeight = newHeight
  }

  updateCornerLayout() {
    if (!this.cornerResizing) return
    if (this.currentWidth) {
      this.sidebarTarget.style.left = `${this.currentLeft}px`
      this.sidebarTarget.style.top = `${this.currentTop}px`
      this.sidebarTarget.style.width = `${this.currentWidth}px`
      this.sidebarTarget.style.height = `${this.currentHeight}px`
      this._repositionHandlesDuringDrag({
        top: this.currentTop,
        left: this.currentLeft,
        width: this.currentWidth,
        height: this.currentHeight
      })
    }
    this.cornerAnimationFrameId = requestAnimationFrame(this.updateCornerLayout.bind(this))
  }

  handleCornerMouseUp() {
    if (!this.cornerResizing) return
    this.cornerResizing = false
    if (this.currentWidth) {
      this.xValue = this.currentLeft
      this.yValue = this.currentTop
      this.widthValue = this.currentWidth
      this.heightValue = this.currentHeight
    }
    this.currentLeft = null
    this.currentTop = null
    this.currentWidth = null
    this.currentHeight = null

    this[`${this.activeCorner}HandleTarget`].classList.remove("active")
    this.activeCorner = null

    cancelAnimationFrame(this.cornerAnimationFrameId)
    this.cornerAnimationFrameId = null

    window.removeEventListener("mousemove", this.boundHandlers.cornerMouseMove)
    window.removeEventListener("mouseup", this.boundHandlers.cornerMouseUp)
    window.removeEventListener("touchmove", this.boundHandlers.cornerMouseMove)
    window.removeEventListener("touchend", this.boundHandlers.cornerMouseUp)

    document.body.style.userSelect = ""
    document.body.style.cursor = ""

    this.sidebarTarget.classList.add("animated")
  }

  // --- Keyboard & Events ---

  handleKeydown(event) {
    const isMod = event.metaKey || event.ctrlKey
    const isPeriod = event.key === "." || event.keyCode === 190
    if (!isMod || !isPeriod) return

    event.preventDefault()
    this.toggle()
  }

  handleToggleRequest() {
    this.toggle()
  }

  handleOpenRequest() {
    if (this.hiddenByRoute) return
    if (!this.effectiveVisible) {
      // Strip transitions for instant open (programmatic opens shouldn't animate)
      const hadAnimation = this.sidebarTarget.classList.contains("animated")
      if (hadAnimation) {
        this.sidebarTarget.classList.remove("animated")
        this.handleTarget.classList.remove("animated")
        document.body.classList.remove("global-sidebar-animated")
      }

      this.setEffectiveVisible(true)
      this.applyState()
      this.applyPageContextOnOpen()

      // Re-enable transitions after paint
      if (hadAnimation) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.sidebarTarget.classList.add("animated")
            this.handleTarget.classList.add("animated")
            document.body.classList.add("global-sidebar-animated")
          })
        })
      }
    }
  }

  renderPrefetchedConversation(frame) {
    const { url, response } = this.prefetchedConversation
    this.prefetchedConversation = null
    const stillWaiting = () => frame.isConnected && !frame.getAttribute("src")

    frame.setAttribute("busy", "")
    response
      .then(result => {
        if (!result.ok) throw Object.assign(new Error(`Conversation prefetch failed with ${result.status}`), { status: result.status })
        return result.text()
      })
      .then(html => {
        if (!/<turbo-stream(?:\s|>)/i.test(html)) throw new Error("Conversation prefetch did not return a Turbo stream")
        frame.removeAttribute("busy")
        if (stillWaiting()) this.renderPrefetchedStream(html, url)
      })
      .catch(error => {
        frame.removeAttribute("busy")
        if (!stillWaiting()) return

        const conversationGone = error.status === 404 && this.defaultConversationUrl
        if (conversationGone) this.forgetConversationUrl()
        frame.setAttribute("src", conversationGone ? this.defaultConversationUrl : url)
        frame.setAttribute("loading", "eager")
      })
    return true
  }

  renderPrefetchedStream(html, url) {
    const markFrameLoadedAfterRender = (event) => {
      if (event.target.getAttribute("target") !== "conversation_frame") return

      const render = event.detail.render
      event.detail.render = async (stream) => {
        await render(stream)
        const frame = this.element.querySelector("#conversation_frame")
        if (frame) this.markConversationFrameLoaded(frame, url)
      }
    }

    document.addEventListener("turbo:before-stream-render", markFrameLoadedAfterRender)
    try {
      window.Turbo.renderStreamMessage(html)
    } finally {
      document.removeEventListener("turbo:before-stream-render", markFrameLoadedAfterRender)
    }
  }

  handlePreloadRequest() {
    if (this.hiddenByRoute || this.effectiveVisible) return

    const ctx = this._pageContext
    if (ctx && ctx.key !== this.anchoredContextKeyValue) return

    if (this.loadConversationFrameIfNeeded()) this.composerPreloadedWhileHidden = true
  }

  focusPreloadedComposer() {
    if (!this.composerPreloadedWhileHidden) return

    this.composerPreloadedWhileHidden = false
    this.element.querySelector("#conversation_frame #message_question")?.focus()
  }

  handleResize() {
    // Clamp a manually-positioned panel so it stays at least MIN_VIEWPORT_MARGIN
    // inside the viewport. Without this, shrinking the window can push the
    // panel partly or fully out of reach.
    if (this.manuallyPositionedValue) {
      const maxX = window.innerWidth - this.widthValue - MIN_VIEWPORT_MARGIN
      const maxY = window.innerHeight - this.effectiveHeight - MIN_VIEWPORT_MARGIN
      const clampedX = Math.max(MIN_VIEWPORT_MARGIN, Math.min(maxX, this.xValue))
      const clampedY = Math.max(MIN_VIEWPORT_MARGIN, Math.min(maxY, this.yValue))
      if (clampedX !== this.xValue) this.xValue = clampedX
      if (clampedY !== this.yValue) this.yValue = clampedY
    }
    // Re-apply state so the effective mode flips between docked and floating
    // as the user crosses MIN_VIEWPORT_WIDTH. The user's manual modeValue is
    // preserved.
    this.applyState()
  }

  handleToggleModeRequest() {
    this.toggleMode()
  }

  handleResetPositionRequest() {
    this.resetPosition()
  }

  // Clear the manual-positioning state and let the panel snap back to its
  // anchored default (top-right with inset). Width/height preferences are
  // preserved; only x/y/manuallyPositioned are reset.
  resetPosition() {
    this.manuallyPositionedValue = false
    this.xValue = 0
    this.yValue = 0
    this.applyState()
  }

  handleTurboLoad() {
    const wasHidden = this.hiddenByRoute
    const wasWriterRoute = this.lastWasWriterRoute
    this.hiddenByRoute = this.isHiddenRoute()
    const isWriterRoute = this.isWriterEditRoute
    this.lastWasWriterRoute = isWriterRoute
    // Re-apply when either the route's hidden-list status or its writer/general
    // classification changes — effectiveVisible reads a different slot in each case.
    if (wasHidden !== this.hiddenByRoute || wasWriterRoute !== isWriterRoute) {
      this.applyState()
    }

    this.reanchorPristineConversationOnNavigation()
  }

  // With the sidebar open, navigating to a different resource normally keeps
  // the loaded conversation anchored to the previous page — you'd close and
  // reopen to re-anchor (see handleOpenRequest/toggle → applyPageContextOnOpen).
  // But when the loaded conversation is still pristine (no messages exchanged,
  // no draft typed) there's nothing to preserve, so we re-anchor live to the
  // resource page just navigated to. A conversation with messages or an unsent
  // draft is left untouched.
  //
  // Navigating to a page with no resource context keeps the current anchor: the
  // assistant does not lose the resource it was aware of just because the user
  // browsed away. The stale anchor is only cleared on an explicit open (close
  // and reopen the sidebar) or when starting a new conversation, so we skip the
  // reset-to-general path here and only re-anchor onto a declared resource.
  reanchorPristineConversationOnNavigation() {
    if (this.hiddenByRoute) return
    if (!this.effectiveVisible) return
    if (!this.loadedConversationIsPristine) return
    if (!this._pageContext) return

    this.applyPageContextOnOpen()
  }

  // A conversation is pristine when its frame holds no exchanged messages, is
  // not mid-response, and has no unsent draft in the composer. Read from the
  // live DOM (not a server stamp) so it stays correct after messages stream
  // into the turbo-permanent frame without a full re-render.
  get loadedConversationIsPristine() {
    const frame = this.element.querySelector("#conversation_frame")
    if (!frame) return false
    if (frame.hasAttribute("busy")) return false
    if (frame.querySelector(".assistant--message[data-message-id]")) return false

    const composer = frame.querySelector("#message_question")
    if (composer && composer.value.trim() !== "") return false

    return true
  }

  isHiddenRoute() {
    const path = window.location.pathname
    if (this.visibleRoutesValue.some(route => path.startsWith(route))) return false
    return this.hiddenRoutesValue.some(route => path.startsWith(route))
  }

  // --- Scroll preservation across Turbo navigations ---
  // turbo-permanent preserves the DOM nodes, but detaching/reattaching the
  // subtree while Turbo swaps bodies causes inner scrollTop values to reset.
  // We back them up onto a module-level Map so the data survives even if
  // Stimulus reinstantiates the controller in the process.

  saveScrollPositions() {
    this.element.querySelectorAll("*").forEach(el => {
      if (el.scrollTop > 0 || el.scrollLeft > 0) {
        SCROLL_BACKUP.set(el, { top: el.scrollTop, left: el.scrollLeft })
      }
    })
  }

  restoreScrollPositions() {
    if (!SCROLL_BACKUP.size) return

    // Defer to the next frame so any layout-induced auto-scroll triggered by
    // the new page's render has settled before we restore.
    requestAnimationFrame(() => {
      for (const [el, saved] of SCROLL_BACKUP) {
        if (!el.isConnected) {
          SCROLL_BACKUP.delete(el)
          continue
        }
        el.scrollTop = saved.top
        el.scrollLeft = saved.left
        SCROLL_BACKUP.delete(el)
      }
    })
  }

  checkRouteVisibility() {
    this.hiddenByRoute = this.isHiddenRoute()
  }

  // Force the lazy conversation frame to fetch when the panel is revealed.
  //
  // The frame is `loading: :lazy` and the panel is hidden with a CSS transform
  // (translateX(100%)) rather than display:none, so it starts off-screen and
  // Turbo defers the fetch via an IntersectionObserver. Revealing the panel is
  // supposed to make the observer fire. But the whole sidebar lives inside a
  // `data-turbo-permanent` element, so across a Turbo Drive navigation the
  // frame node is detached and reattached; the observer does not reliably
  // re-arm, so revealing the panel no longer triggers the deferred load and the
  // spinner spins forever (a full page reload creates a fresh frame and fixes
  // it). Triggering the load explicitly on open makes it deterministic.
  loadConversationFrameIfNeeded() {
    const frame = this.element.querySelector("#conversation_frame")
    if (!frame) return false
    if (this.prefetchedConversation) return this.renderPrefetchedConversation(frame)
    // Idempotency guard shared with tabs_controller / anchor_scroll_navigation_controller:
    // `dataset.lazyLoaded` is set on `turbo:frame-load` (see handleFrameLoad) and is the
    // reliable "already loaded" signal — re-triggering would wipe the conversation.
    // `busy` means a fetch is already in flight.
    if (frame.dataset.lazyLoaded === "true" || frame.hasAttribute("busy")) return false
    if (!frame.getAttribute("src")) return false
    // Unlike the sibling controllers we can't use `frame.src = frame.src`: that only
    // reloads a frame Turbo has already loaded once, so it never kicks the *first*
    // load of a still-lazy frame. Those controllers get away with it because they
    // toggle `display`, which re-arms the appearance observer; here the transform-based
    // hiding does not. Flipping loading to eager makes Turbo load the source URL now.
    if (frame.getAttribute("loading") !== "eager") frame.setAttribute("loading", "eager")
    return true
  }

  // Hovering or tabbing onto the trigger (or the suggestions rail) is the first
  // sign the user is heading for the panel. `conversationPrefetched` short-
  // circuits the rest of the pointer moves once warming is done or moot.
  handlePrefetchIntent(event) {
    if (this.conversationPrefetched) return

    const target = event.target
    if (!target || typeof target.closest !== "function") return
    if (!target.closest("#assistant-sidebar-trigger, .assistant-suggestions-rail")) return

    this.prefetchConversationFrame()
  }

  // Warm the conversation panel before the user opens it. The panel is a lazy
  // turbo frame hidden off-screen by a transform, so it only fetches on open,
  // leaving the empty-state suggestions to arrive a beat later. Warming it on
  // intent means the frame and its chips are usually ready by the time the
  // click lands. Idempotent: the load guard here and the matching-anchor check
  // on open make the eventual open a no-op, so a prefetch that never converts
  // costs one request.
  prefetchConversationFrame() {
    if (this.hiddenByRoute) return

    const frame = this.element.querySelector("#conversation_frame")
    if (!frame) return
    if (frame.dataset.lazyLoaded === "true" || frame.hasAttribute("busy")) {
      this.conversationPrefetched = true
      return
    }
    // Already open: the open path loads the frame itself, so there is nothing to
    // warm now. Left unflagged so a later close/reopen still benefits.
    if (this.effectiveVisible) return

    // Anchor to the resource on screen before loading, so the warmed frame holds
    // this page's chips rather than a general conversation the open would then
    // replace — the same anchoring open performs in applyPageContextOnOpen.
    const ctx = this._pageContext
    if (ctx && ctx.key !== this.anchoredContextKeyValue) {
      frame.setAttribute("src", ctx.url)
      this.conversationUrlValue = ctx.url
      this.anchoredContextKeyValue = ctx.key
    }

    this.loadConversationFrameIfNeeded()
    this.conversationPrefetched = true
  }

  handleFrameLoad(event) {
    const frame = event.target
    if (frame.id !== "conversation_frame") return

    this.markConversationFrameLoaded(frame, frame.getAttribute("src"))
  }

  markConversationFrameLoaded(frame, src) {
    // Mark the frame loaded so loadConversationFrameIfNeeded never re-triggers it.
    frame.dataset.lazyLoaded = "true"
    if (src) this.conversationUrlValue = src

    // Track which resource the loaded conversation is anchored to (stamped by
    // conversations/show_tools) so opening the sidebar on a resource page can
    // decide reuse-vs-new. Read from an inner element rather than the frame
    // tag so it doesn't depend on Turbo's frame-attribute merge behavior.
    const stamp = frame.querySelector("[data-anchored-context-key]")
    this.anchoredContextKeyValue = stamp?.getAttribute("data-anchored-context-key") || ""
  }

  handleFrameMissing(event) {
    const frame = event.target
    if (frame.id !== "conversation_frame") return

    // Restored URL returned a response without a matching frame (e.g., 404 for
    // a deleted conversation). Reset to the default and clear the saved URL.
    event.preventDefault()
    this.conversationUrlValue = ""
    const writerDocumentId = writerDocumentIdOf(frame.getAttribute("src"))
    if (writerDocumentId) forgetWriterConversation(writerDocumentId)

    if (!this.defaultConversationUrl) return
    // Re-pointing the frame at the src that just came back frameless would
    // refetch it forever, saturating the browser and the origin.
    if (frame.getAttribute("src") === this.defaultConversationUrl) return

    frame.setAttribute("src", this.defaultConversationUrl)
  }

  forgetConversationUrl() {
    this.conversationUrlValue = ""
  }

  // Starts a fresh conversation from the sidebar's "+" button. When the current
  // page declares a resource, anchors the new conversation to it so a
  // conversation created on a resource page is aware of that resource. This
  // overrides the link's default target, which carries the *previous*
  // conversation's params rather than the page the user is on now (e.g. a
  // started conversation anchored to tender A, while the user has since browsed
  // to tender B). On a page with no resource context the link's default general
  // new-conversation navigation proceeds unchanged.
  handleNewConversationRequest(event) {
    this.forgetConversationUrl()
    if (this.pageWriterDocumentId) forgetWriterConversation(this.pageWriterDocumentId)

    const ctx = this._pageContext
    if (!ctx) return

    const frame = this.element.querySelector("#conversation_frame")
    if (!frame) return

    event.preventDefault()
    frame.setAttribute("src", ctx.url)
    this.conversationUrlValue = ctx.url
    this.anchoredContextKeyValue = ctx.key
  }

  restoreConversationUrl() {
    const frame = this.element.querySelector("#conversation_frame")
    if (!frame) return

    // Persist the default src on the DOM so it survives turbo-permanent reconnects
    // (each reconnect creates a fresh controller instance, but the dataset is preserved).
    if (!this.element.dataset.globalSidebarDefaultSrc) {
      this.element.dataset.globalSidebarDefaultSrc = frame.getAttribute("src") || frame.dataset.defaultSrc || ""
    }
    this.defaultConversationUrl = this.element.dataset.globalSidebarDefaultSrc

    // Only apply the saved URL on the first connect — subsequent reconnects
    // already have the live frame state.
    if (this.element.dataset.globalSidebarConnected) return
    this.element.dataset.globalSidebarConnected = "true"

    this.prefetchedConversation = window.globalSidebarPrefetch
    window.globalSidebarPrefetch = null
    if (this.prefetchedConversation) return

    const pageWriterDocumentId = this.pageWriterDocumentId
    if (pageWriterDocumentId) {
      const src = rememberedWriterConversation(pageWriterDocumentId)
      if (src) frame.setAttribute("src", src)
      return
    }

    if (!this.isConversationUrl(this.conversationUrlValue)) {
      if (this.conversationUrlValue) this.forgetConversationUrl()
      return
    }

    frame.setAttribute("src", this.withoutWriterDocument(this.conversationUrlValue))
  }

  conversationUrlValueChanged(url) {
    rememberWriterConversation(url)
  }

  isConversationUrl(value) {
    if (!value) return false

    const url = new URL(value, window.location.origin)
    return url.origin === window.location.origin && url.pathname.startsWith("/asistente/")
  }

  // Reads the current page's resource-context declaration (rendered outside the
  // turbo-permanent sidebar, so it reflects the page actually on screen).
  // Returns { key, url } or null when the page declares no supported resource.
  get _pageContext() {
    const el = document.querySelector("[data-assistant-page-context-key]")
    if (!el) return null

    const key = el.getAttribute("data-assistant-page-context-key")
    const url = el.getAttribute("data-assistant-page-context-url")
    if (!key || !url) return null

    return { key, url }
  }

  // When the sidebar is opened on a page that declares a resource context,
  // anchor the conversation to that resource: reuse the loaded conversation if
  // it's already the same resource, otherwise load a fresh resource-anchored
  // new-conversation form (the previously loaded conversation is left intact).
  // On a page with no resource context, a still-pristine conversation drops its
  // stale anchor back to a general conversation (see resetPristineAnchorToGeneral).
  applyPageContextOnOpen() {
    const ctx = this._pageContext
    if (!ctx) {
      this.resetPristineAnchorToGeneral()
      return
    }
    if (ctx.key === this.anchoredContextKeyValue) return

    const frame = this.element.querySelector("#conversation_frame")
    if (!frame) return

    frame.setAttribute("src", ctx.url)
    this.conversationUrlValue = ctx.url
    this.anchoredContextKeyValue = ctx.key
  }

  // On a page that declares no resource context, a still-pristine conversation
  // anchored to a (now stale) resource drops back to a general conversation, so
  // its context pill doesn't linger from a page the user has left. A conversation
  // with messages or a draft is kept so it stays resumable from anywhere, and the
  // writer edit route is exempt — it manages its own frame and writer_document_id.
  resetPristineAnchorToGeneral() {
    if (this.isWriterEditRoute) return
    if (!this.anchoredContextKeyValue) return
    if (!this.loadedConversationIsPristine) return

    const url = this.generalConversationUrl
    if (!url) return

    // loadedConversationIsPristine already guaranteed the frame exists.
    const frame = this.element.querySelector("#conversation_frame")
    frame.setAttribute("src", url)
    this.conversationUrlValue = url
    this.anchoredContextKeyValue = ""
  }

  // The fresh, unanchored new-conversation URL: the sidebar's default src with
  // any resource/writer prompt_params stripped off.
  get generalConversationUrl() {
    const base = this.defaultConversationUrl || this.conversationUrlValue
    if (!base) return null

    const url = new URL(base, window.location.origin)
    for (const key of Array.from(url.searchParams.keys())) {
      if (key.startsWith("prompt_params")) url.searchParams.delete(key)
    }
    return url.pathname + url.search
  }

  get pageWriterDocumentId() {
    const writerRoot = document.querySelector("[data-writer-sidebar-tabs-writer-document-id-value]")
    return writerRoot?.dataset.writerSidebarTabsWriterDocumentIdValue || null
  }

  withoutWriterDocument(src) {
    const url = new URL(src, window.location.origin)
    url.searchParams.delete(WRITER_DOCUMENT_PARAM)
    return url.pathname + url.search
  }
}
